import type { AudioSampleMetadata, VoiceProfile } from "../types/app";
import { defaultVoiceProfile, nowIso } from "../constants";
import { createId } from "../utils/id";
import { clampVoiceProfile } from "./voiceSettings";

export async function analyzeAudioSample(file: File): Promise<AudioSampleMetadata> {
  const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return { duration: 0, rms: 0, estimatedPitch: 0, zeroCrossingRate: 0, spectralCentroid: 0, silenceRatio: 0, tempoHint: 0 };
  }
  const context = new AudioContextCtor();
  try {
    const buffer = await context.decodeAudioData(await file.arrayBuffer());
    const channel = buffer.getChannelData(0);
    const step = Math.max(1, Math.floor(channel.length / 48000));
    let sumSquares = 0;
    let crossings = 0;
    let silent = 0;
    let previous = channel[0] ?? 0;
    for (let index = 0; index < channel.length; index += step) {
      const sample = channel[index] ?? 0;
      sumSquares += sample * sample;
      if ((sample >= 0 && previous < 0) || (sample < 0 && previous >= 0)) crossings += 1;
      if (Math.abs(sample) < 0.015) silent += 1;
      previous = sample;
    }
    const count = Math.ceil(channel.length / step);
    const rms = Math.sqrt(sumSquares / Math.max(count, 1));
    const zeroCrossingRate = crossings / Math.max(count, 1);
    const estimatedPitch = Math.min(Math.max(zeroCrossingRate * buffer.sampleRate * 0.5, 80), 420);
    const spectralCentroid = Math.min(8000, Math.max(200, estimatedPitch * 5.5 + rms * 1800));
    const silenceRatio = silent / Math.max(count, 1);
    return {
      duration: buffer.duration,
      rms,
      estimatedPitch,
      zeroCrossingRate,
      spectralCentroid,
      silenceRatio,
      tempoHint: Math.max(0.4, Math.min(1.4, 1.05 - silenceRatio * 0.4 + rms * 0.2)),
    };
  } finally {
    await context.close().catch(() => undefined);
  }
}

export function metadataToVoiceProfile(name: string, sourceName: string, sourceLicense: string, metadata: AudioSampleMetadata): VoiceProfile {
  const base = defaultVoiceProfile();
  const pitch = metadata.estimatedPitch > 210 ? 1.14 : metadata.estimatedPitch < 150 ? 0.95 : 1.05;
  return clampVoiceProfile({
    ...base,
    id: createId("voice"),
    name,
    sourceName,
    sourceLicense,
    pitch,
    rate: metadata.tempoHint || base.rate,
    gain: Math.min(1.2, Math.max(0.65, 0.85 + metadata.rms)),
    warmth: metadata.spectralCentroid < 1800 ? 0.7 : 0.45,
    brightness: metadata.spectralCentroid > 2400 ? 0.65 : 0.38,
    softness: Math.min(0.9, 0.55 + metadata.silenceRatio * 0.2),
    breathiness: Math.min(0.7, metadata.zeroCrossingRate * 6),
    compression: 0.45,
    reverb: 0.1,
    pauseLength: Math.min(0.65, metadata.silenceRatio * 0.7),
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });
}
